import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { reduxForm, change } from 'redux-form';
import { injectIntl } from 'react-intl';
import { TaskCompletionStatus } from '../types';
import { toString } from '../../../core/utils';
import { TaskType } from '../models';

const TodaysTasksForm = ({ task, status, employeeName, dispatch, intl }) => {
  const taskList = task.tasks || [];

  const getTaskTypeDisplayName = (type) => {
    const taskTypeString = toString(TaskType, type);
    const taskTypeMap = {
      Maintenance: intl.formatMessage({ id: 'maintenance' }),
      Project: intl.formatMessage({ id: 'workOrder', defaultMessage: 'Work Order' }),
      OpenChecklist: intl.formatMessage({ id: 'openChecklist' }),
      ClosingChecklist: intl.formatMessage({ id: 'closingChecklist' }),
      PreventativeMaintenance: intl.formatMessage({ id: 'preventativeMaintenance' }),
      CleaningList: intl.formatMessage({ id: 'cleaningList' }),
      HourlyInspection: intl.formatMessage({ id: 'hourlyInspection' }),
      ExpressInterior: intl.formatMessage({ id: 'expressInterior' }),
    };
    return taskTypeMap[taskTypeString] || taskTypeString;
  };

  const [typeFilters, setTypeFilters] = useState(() => {
    const types = [...new Set(taskList.map((t) => t.type))];
    return types.reduce((acc, type) => ({ ...acc, [type]: true }), {});
  });

  const handleCheckboxChange = (type) => {
    setTypeFilters((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const filteredTasks = taskList.filter((t) => typeFilters[t.type]);
  const filteredTaskIds = filteredTasks.map((t) => t.id);
  const taskAmount = filteredTaskIds.length;

  // Update form values whenever filteredTasks changes
  useEffect(() => {
    if (dispatch) {
      dispatch(change('todaysTasksForm', 'taskIds', filteredTaskIds));
      dispatch(change('todaysTasksForm', 'filteredTasks', filteredTasks));
    }
  }, [filteredTasks, dispatch]);

  // expose submit() to parent via ref

  return (
    <div className="form-group">
      <div>
        {employeeName && (
          <label>
            {intl.formatMessage({ id: 'teamMember' })}: {employeeName}
          </label>
        )}
      </div>
      <label>
        {status === TaskCompletionStatus.Snoozed
          ? intl.formatMessage({ id: 'taskSnoozeConfirmation' }, { count: taskAmount })
          : intl.formatMessage({ id: 'taskCompletionConfirmation' }, { count: taskAmount })}
      </label>

      <div style={{ marginTop: '10px' }}>
        <strong>Filter by Task Type:</strong>
        <div>
          {Object.keys(typeFilters).map((type) => (
            <label key={type} style={{ display: 'block', margin: '4px 0' }}>
              <input
                type="checkbox"
                checked={typeFilters[type]}
                onChange={() => handleCheckboxChange(type)}
              />{' '}
              {getTaskTypeDisplayName(type)}
            </label>
          ))}
        </div>
      </div>

      {filteredTasks.length > 0 && (
        <ul style={{ marginTop: '10px' }}>
          {filteredTasks.map((t) => (
            <li key={t.id}>
              {t.name} <em>({getTaskTypeDisplayName(t.type)})</em>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

TodaysTasksForm.propTypes = {
  task: PropTypes.shape({
    tasks: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  status: PropTypes.number.isRequired,
  employeeName: PropTypes.string,
  dispatch: PropTypes.func,
  intl: PropTypes.object.isRequired,
};

export default injectIntl(reduxForm({ form: 'todaysTasksForm' })(TodaysTasksForm));
